import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed'
import FilterListIcon from '@mui/icons-material/FilterList'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import BigModal from '../../../../components/Modals/BigModal'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import { getCurrencySymbol } from '../../../../helpers/currency'
import { EVENT_ROLE_OPTIONS } from '../AddNewShift'
import {
  buildChangesPayload,
  buildTimesPayload,
  computeShiftTimes,
  emptyChanges,
  endsBeforeStart,
  emptyFilters,
  filterShifts,
  getChangesError,
  getFilterOptions,
  hasAnyChange,
  isShiftCovered,
  MASS_UPDATE_MAX_SHIFTS,
  previewShift,
  shiftLockReason,
  STATUS_FILTERS,
} from './massUpdate'

const assigneeName = (shift) => {
  const userAccount =
    shift?.assignedTo?.userAccount || shift?.assignedSubcontractor?.userAccount
  if (userAccount) {
    return `${userAccount.firstName ?? ''} ${userAccount.lastName ?? ''}`.trim()
  }
  if (shift?.assignedToSubcontractor) return 'Subcontractor'
  return ''
}

const Section = ({ step, icon, title, caption, action, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, height: '100%' }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} mb={1.75}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          bgcolor: 'anMuted.main',
          color: 'contrast.main',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
          {step}. {title}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    <Stack spacing={1.75}>{children}</Stack>
  </Paper>
)

const FilterGroup = ({ label, options, selected, onToggle, format }) => {
  if (options.length < 2) return null
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        display="block"
        mb={0.5}
      >
        {label}
      </Typography>
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {options.map((option) => {
          const active = selected.includes(option.value)
          return (
            <Chip
              key={option.value}
              size="small"
              clickable
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => onToggle(option.value)}
              label={
                <>
                  {format ? format(option.value) : option.value || 'None'}
                  <Box component="span" sx={{ opacity: 0.65, ml: 0.5 }}>
                    {option.total}
                  </Box>
                </>
              }
            />
          )
        })}
      </Stack>
    </Box>
  )
}

// One optional change: a switch, and its fields once switched on
const ChangeRow = ({ title, caption, on, onToggle, children }) => (
  <Box
    sx={{
      border: 1,
      borderColor: on ? 'primary.main' : 'divider',
      borderRadius: 2,
      px: 1.5,
      py: 1,
      bgcolor: on ? 'background.default' : 'background.paper',
    }}
  >
    <FormControlLabel
      sx={{ mx: 0, width: '100%', alignItems: 'flex-start' }}
      control={
        <Switch
          size="small"
          checked={on}
          onChange={(e) => onToggle(e.target.checked)}
        />
      }
      label={
        <Box ml={0.5} mt={0.25}>
          <Typography variant="body2" fontWeight={700}>
            {title}
          </Typography>
          {caption && (
            <Typography variant="caption" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Box>
      }
    />
    <Collapse in={on} unmountOnExit>
      <Box pt={1.25} pb={0.5}>
        {children}
      </Box>
    </Collapse>
  </Box>
)

// "Sat 10 Oct 07:30–19:30", with the date only when it moves and "(+1)" when
// the shift ends on a later day
const formatNextTimes = (shift, nextTimes) => {
  const dayGap = nextTimes.end
    .clone()
    .startOf('day')
    .diff(nextTimes.start.clone().startOf('day'), 'days')
  return `${
    nextTimes.start.isSame(shift.start, 'day')
      ? ''
      : `${nextTimes.start.format('ddd D MMM')} `
  }${nextTimes.start.format('HH:mm')}–${nextTimes.end.format('HH:mm')}${
    dayGap > 0 ? ` (+${dayGap})` : ''
  }`
}

const ShiftRow = ({
  shift,
  checked,
  onToggle,
  nextTimes,
  changed,
  issues,
  showChange,
  invalid,
}) => {
  const lock = shiftLockReason(shift)
  const name = assigneeName(shift)
  const timesMoved = changed?.includes('times') || (invalid && !lock)
  const flagged = issues?.length || (invalid && checked && !lock)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1,
        py: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        opacity: lock ? 0.6 : 1,
        bgcolor: flagged ? 'rgba(211, 47, 47, 0.06)' : 'transparent',
      }}
    >
      <Checkbox
        size="small"
        checked={checked && !lock}
        disabled={!!lock}
        onChange={onToggle}
        sx={{ p: 0.5 }}
        inputProps={{ 'aria-label': `Select shift ${moment(shift.start).format('D MMM HH:mm')}` }}
      />
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography variant="body2" fontWeight={700}>
            {moment(shift.start).format('ddd D MMM')}
          </Typography>
          <Typography
            variant="body2"
            sx={timesMoved ? { textDecoration: 'line-through', color: 'text.secondary' } : null}
          >
            {moment(shift.start).format('HH:mm')}–{moment(shift.end).format('HH:mm')}
          </Typography>
          {timesMoved && nextTimes && (
            <>
              <ArrowRightAltIcon fontSize="small" color="primary" />
              <Typography
                variant="body2"
                fontWeight={700}
                color={invalid ? 'error.main' : 'primary.main'}
              >
                {formatNextTimes(shift, nextTimes)}
              </Typography>
            </>
          )}
        </Stack>
        {invalid && checked && !lock && (
          <Typography variant="caption" color="error.main" display="block">
            Would end before it starts — adjust the times or untick this shift.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {[shift.clinicalRole, shift.eventAssignment].filter(Boolean).join(' · ')}
          {' · '}
          {name || 'Unassigned'}
        </Typography>
        {issues?.map((issue, index) => (
          <Typography key={index} variant="caption" color="error.main" display="block">
            {issue.message}
          </Typography>
        ))}
      </Box>
      <Stack direction="row" gap={0.5} flexShrink={0} alignItems="center">
        {lock ? (
          <Tooltip title={`${lock} shifts can't be changed`}>
            <Chip
              size="small"
              icon={<LockOutlinedIcon />}
              label={lock}
              sx={{ height: 22, fontSize: 11 }}
            />
          </Tooltip>
        ) : (
          checked &&
          showChange &&
          (invalid ? (
            <Chip
              size="small"
              color="error"
              label="Invalid times"
              sx={{ height: 22, fontSize: 11 }}
            />
          ) : changed?.length ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={changed.join(', ')}
              sx={{ height: 22, fontSize: 11, maxWidth: 170 }}
            />
          ) : (
            <Chip size="small" label="No change" sx={{ height: 22, fontSize: 11 }} />
          ))
        )}
      </Stack>
    </Box>
  )
}

const MassUpdateShifts = ({ open, onClose, shifts, eventTitle, eventID, onUpdated }) => {
  const { setToast } = useContext(GlobalContext)
  const orgSettings = useContext(TenantSettingsContext).orgConfig?.orgSettings
  const currency = getCurrencySymbol(orgSettings?.currency ?? '£')
  const roles = orgSettings?.roles ?? []
  const defaultPayRates = orgSettings?.defaultPayRates ?? {}

  const [filters, setFilters] = useState(emptyFilters)
  const [selected, setSelected] = useState(() => new Set())
  const [changes, setChanges] = useState(emptyChanges)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [issuesByShift, setIssuesByShift] = useState({})

  // Fresh start each time the popup opens: every editable shift selected
  useEffect(() => {
    if (!open) return
    setFilters(emptyFilters())
    setChanges(emptyChanges())
    setSelected(
      new Set(shifts.filter((shift) => !shiftLockReason(shift)).map((s) => s._id)),
    )
    setError('')
    setIssuesByShift({})
  }, [open])

  // Server issues describe the last request - drop them once anything changes
  useEffect(() => {
    setIssuesByShift({})
    setError('')
  }, [changes, selected])

  const sortedShifts = useMemo(
    () =>
      [...shifts].sort(
        (a, b) =>
          moment(a.start).valueOf() - moment(b.start).valueOf() ||
          (a.clinicalRole ?? '').localeCompare(b.clinicalRole ?? ''),
      ),
    [shifts],
  )
  const options = useMemo(() => getFilterOptions(sortedShifts), [sortedShifts])
  const visible = useMemo(
    () => filterShifts(sortedShifts, filters),
    [sortedShifts, filters],
  )
  const visibleEditable = visible.filter((shift) => !shiftLockReason(shift))
  // Only shifts that are both ticked and match the filters are updated
  const targets = visibleEditable.filter((shift) => selected.has(shift._id))

  const changesPayload = useMemo(
    () => buildChangesPayload(changes, defaultPayRates),
    [changes, defaultPayRates],
  )
  const anyChange = hasAnyChange(changes)
  const changesError = anyChange ? getChangesError(changes) : null

  const previews = useMemo(() => {
    const map = new Map()
    visible.forEach((shift) => {
      const nextTimes = computeShiftTimes(shift, changes.times)
      map.set(shift._id, {
        nextTimes,
        invalid: endsBeforeStart(nextTimes),
        changed: changesError ? [] : previewShift(shift, changesPayload, nextTimes),
      })
    })
    return map
  }, [visible, changes.times, changesPayload, changesError])

  const willChange = targets.filter(
    (shift) => previews.get(shift._id)?.changed.length,
  )
  const invalidCount = targets.filter(
    (shift) => previews.get(shift._id)?.invalid,
  ).length
  const lockedCount = visible.length - visibleEditable.length
  const assignedTimeChanges = willChange.filter(
    (shift) =>
      isShiftCovered(shift) && previews.get(shift._id)?.changed.includes('times'),
  ).length
  const filtersActive =
    filters.days.length ||
    filters.roles.length ||
    filters.eventRoles.length ||
    filters.patterns.length ||
    filters.status !== 'all'

  const toggleFilter = (key) => (value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((item) => item !== value)
        : [...prev[key], value],
    }))

  const toggleShift = (id) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allVisibleSelected =
    visibleEditable.length > 0 &&
    visibleEditable.every((shift) => selected.has(shift._id))
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      visibleEditable.forEach((shift) =>
        allVisibleSelected ? next.delete(shift._id) : next.add(shift._id),
      )
      return next
    })

  const setChange = (key, patch) =>
    setChanges((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  const handleSubmit = async () => {
    if (changesError) {
      setError(changesError)
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await API.post(`/events/${eventID}/shifts/mass-update`, {
        shiftIds: willChange.map((shift) => shift._id),
        changes: changesPayload,
        times: buildTimesPayload(willChange, changes.times),
      })
      if (res.status < 200 || res.status >= 300) {
        const grouped = {}
        ;(res.data?.issues ?? []).forEach((issue) => {
          if (!issue?.shift) return
          grouped[issue.shift] = [...(grouped[issue.shift] ?? []), issue]
        })
        setIssuesByShift(grouped)
        setError(
          res.data?.error ||
            res.data?.message ||
            'Unable to update the shifts. Please try again.',
        )
        return
      }
      const updated = res.data?.updated ?? willChange.length
      setToast({
        message: `Updated ${updated} shift${updated === 1 ? '' : 's'}`,
      })
      onUpdated?.()
      onClose()
    } catch (err) {
      console.error(err)
      setError('Unable to update the shifts. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const flaggedCount = Object.keys(issuesByShift).length

  return (
    <BigModal
      open={open}
      onClose={onClose}
      style={{
        padding: 0,
        width: { xs: 'calc(100vw - 24px)', sm: 'min(1180px, 94vw)' },
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxHeight: '90vh',
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: { xs: 2, sm: 3 }, py: 1.75, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Avatar sx={{ bgcolor: 'contrast.main', width: 38, height: 38 }}>
          <DynamicFeedIcon fontSize="small" />
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            Mass update shifts
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {eventTitle} · {shifts.length} shift{shifts.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, sm: 2 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <Section
              step={1}
              icon={<FilterListIcon fontSize="small" />}
              title="Choose shifts"
              caption="Narrow down by day, role or status, then tick the ones to change"
              action={
                filtersActive ? (
                  <Button size="small" color="inherit" onClick={() => setFilters(emptyFilters())}>
                    Clear filters
                  </Button>
                ) : null
              }
            >
              <FilterGroup
                label="Day"
                options={options.days}
                selected={filters.days}
                onToggle={toggleFilter('days')}
                format={(value) => moment(value, 'YYYY-MM-DD').format('ddd D MMM')}
              />
              <FilterGroup
                label="Clinical role"
                options={options.roles}
                selected={filters.roles}
                onToggle={toggleFilter('roles')}
              />
              <FilterGroup
                label="Event role"
                options={options.eventRoles}
                selected={filters.eventRoles}
                onToggle={toggleFilter('eventRoles')}
              />
              <FilterGroup
                label="Shift times"
                options={options.patterns}
                selected={filters.patterns}
                onToggle={toggleFilter('patterns')}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  mb={0.5}
                >
                  Status
                </Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {STATUS_FILTERS.map((status) => (
                    <Chip
                      key={status.value}
                      size="small"
                      clickable
                      label={status.label}
                      color={filters.status === status.value ? 'primary' : 'default'}
                      variant={filters.status === status.value ? 'filled' : 'outlined'}
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, status: status.value }))
                      }
                    />
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ px: 1, py: 0.5, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}
                >
                  <Checkbox
                    size="small"
                    sx={{ p: 0.5 }}
                    checked={allVisibleSelected}
                    indeterminate={!allVisibleSelected && targets.length > 0}
                    disabled={!visibleEditable.length}
                    onChange={toggleAllVisible}
                    inputProps={{ 'aria-label': 'Select all shown shifts' }}
                  />
                  <Typography variant="body2" fontWeight={700} flex={1}>
                    {targets.length} of {visible.length} shown selected
                  </Typography>
                  {lockedCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {lockedCount} locked
                    </Typography>
                  )}
                </Stack>
                <Box sx={{ maxHeight: { xs: 320, lg: 400 }, overflowY: 'auto' }}>
                  {!visible.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      No shifts match these filters.
                    </Typography>
                  ) : (
                    visible.map((shift) => (
                      <ShiftRow
                        key={shift._id}
                        shift={shift}
                        checked={selected.has(shift._id)}
                        onToggle={() => toggleShift(shift._id)}
                        nextTimes={previews.get(shift._id)?.nextTimes}
                        changed={previews.get(shift._id)?.changed}
                        issues={issuesByShift[shift._id]}
                        invalid={previews.get(shift._id)?.invalid}
                        showChange={anyChange && !changesError}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Section>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Section
              step={2}
              icon={<TuneIcon fontSize="small" />}
              title="Choose changes"
              caption="Only the changes you switch on are applied — everything else is left as it is"
            >
              <Box
                sx={{
                  border: 1,
                  borderColor: changes.times.mode !== 'none' ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.25,
                  bgcolor: changes.times.mode !== 'none' ? 'background.default' : 'background.paper',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Times
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Change dates and times, or move shifts
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={changes.times.mode}
                    onChange={(e, mode) => mode && setChange('times', { mode })}
                  >
                    <ToggleButton value="none">Keep</ToggleButton>
                    <ToggleButton value="set">Set times</ToggleButton>
                    <ToggleButton value="move">Move by</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Collapse in={changes.times.mode === 'set'} unmountOnExit>
                  <Stack gap={1.5} pt={1.5}>
                    {[
                      { key: 'start', label: 'Start' },
                      { key: 'end', label: 'End' },
                    ].map(({ key, label }) => (
                      <Stack key={key} direction="row" gap={1.5}>
                        <TextField
                          type="date"
                          size="small"
                          label={`${label} date`}
                          value={changes.times[`${key}Date`]}
                          onChange={(e) =>
                            setChange('times', { [`${key}Date`]: e.target.value })
                          }
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        <TextField
                          type="time"
                          size="small"
                          label={`${label} time`}
                          value={changes.times[key]}
                          onChange={(e) =>
                            setChange('times', { [key]: e.target.value })
                          }
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Stack>
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      Leave any field blank to keep each shift&apos;s own value.
                      A blank end date keeps the shift&apos;s length in days, so
                      overnight shifts stay overnight.
                    </Typography>
                  </Stack>
                </Collapse>
                <Collapse in={changes.times.mode === 'move'} unmountOnExit>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} pt={1.5}>
                    <TextField
                      type="number"
                      size="small"
                      label="Minutes"
                      value={changes.times.minutes}
                      onChange={(e) => setChange('times', { minutes: e.target.value })}
                      inputProps={{ min: 1, step: 15 }}
                      sx={{ minWidth: 110 }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Direction"
                      value={changes.times.direction}
                      onChange={(e) => setChange('times', { direction: e.target.value })}
                      fullWidth
                    >
                      <MenuItem value="later">Later</MenuItem>
                      <MenuItem value="earlier">Earlier</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Apply to"
                      value={changes.times.target}
                      onChange={(e) => setChange('times', { target: e.target.value })}
                      fullWidth
                    >
                      <MenuItem value="both">Start and end</MenuItem>
                      <MenuItem value="start">Start only</MenuItem>
                      <MenuItem value="end">End only</MenuItem>
                    </TextField>
                  </Stack>
                </Collapse>
              </Box>

              <ChangeRow
                title="Clinical role"
                on={changes.role.on}
                onToggle={(on) => setChange('role', { on })}
              >
                <Stack gap={1}>
                  <TextField
                    select
                    size="small"
                    label="New role"
                    value={changes.role.value}
                    onChange={(e) => setChange('role', { value: e.target.value })}
                    fullWidth
                  >
                    {roles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                  {changes.role.value &&
                    defaultPayRates[changes.role.value] !== undefined &&
                    !changes.pay.on && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={changes.role.applyDefaultRate}
                            onChange={(e) =>
                              setChange('role', { applyDefaultRate: e.target.checked })
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Also use the role&apos;s default rate ({currency}
                            {defaultPayRates[changes.role.value]}/h)
                          </Typography>
                        }
                      />
                    )}
                </Stack>
              </ChangeRow>

              <ChangeRow
                title="Pay rate"
                on={changes.pay.on}
                onToggle={(on) => setChange('pay', { on })}
              >
                <Stack direction="row" gap={1.5} alignItems="center">
                  <TextField
                    type="number"
                    size="small"
                    label="Rate"
                    value={changes.pay.rate}
                    onChange={(e) => setChange('pay', { rate: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{currency}</InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={changes.pay.hourly ? 'hourly' : 'fixed'}
                    sx={{ flexShrink: 0, '& .MuiToggleButton-root': { whiteSpace: 'nowrap' } }}
                    onChange={(e, value) => value && setChange('pay', { hourly: value === 'hourly' })}
                  >
                    <ToggleButton value="hourly">Per hour</ToggleButton>
                    <ToggleButton value="fixed">Per shift</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </ChangeRow>

              <ChangeRow
                title="Event role"
                caption="e.g. Ambulance, Foot Patrol"
                on={changes.eventRole.on}
                onToggle={(on) => setChange('eventRole', { on })}
              >
                <Autocomplete
                  freeSolo
                  size="small"
                  options={EVENT_ROLE_OPTIONS}
                  value={changes.eventRole.value}
                  onChange={(e, value) => setChange('eventRole', { value: value ?? '' })}
                  onInputChange={(e, value) => setChange('eventRole', { value: value ?? '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Event role" helperText="Leave blank to clear it" />
                  )}
                />
              </ChangeRow>

              <ChangeRow
                title="Staff can book"
                caption="Whether open shifts are advertised to staff"
                on={changes.bookable.on}
                onToggle={(on) => setChange('bookable', { on })}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={changes.bookable.value ? 'yes' : 'no'}
                  onChange={(e, value) => value && setChange('bookable', { value: value === 'yes' })}
                >
                  <ToggleButton value="yes">Bookable</ToggleButton>
                  <ToggleButton value="no">Not bookable</ToggleButton>
                </ToggleButtonGroup>
              </ChangeRow>

              <ChangeRow
                title="Shift notes"
                on={changes.note.on}
                onToggle={(on) => setChange('note', { on })}
              >
                <Stack gap={1.25}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={changes.note.mode}
                    onChange={(e, mode) => mode && setChange('note', { mode })}
                  >
                    <ToggleButton value="append">Add to notes</ToggleButton>
                    <ToggleButton value="replace">Replace</ToggleButton>
                    <ToggleButton value="clear">Clear</ToggleButton>
                  </ToggleButtonGroup>
                  {changes.note.mode !== 'clear' && (
                    <TextField
                      size="small"
                      label="Note"
                      value={changes.note.text}
                      onChange={(e) => setChange('note', { text: e.target.value })}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  )}
                </Stack>
              </ChangeRow>

              <ChangeRow
                title="Fixed bonus"
                caption="e.g. vehicle pickup, early start"
                on={changes.bonus.on}
                onToggle={(on) => setChange('bonus', { on })}
              >
                <Stack gap={1.25}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={changes.bonus.mode}
                    onChange={(e, mode) => mode && setChange('bonus', { mode })}
                  >
                    <ToggleButton value="add">Add a bonus</ToggleButton>
                    <ToggleButton value="clear">Remove all bonuses</ToggleButton>
                  </ToggleButtonGroup>
                  {changes.bonus.mode === 'add' && (
                    <Stack direction="row" gap={1.5}>
                      <TextField
                        size="small"
                        label="Label"
                        value={changes.bonus.label}
                        onChange={(e) => setChange('bonus', { label: e.target.value })}
                        fullWidth
                      />
                      <TextField
                        type="number"
                        size="small"
                        label="Amount"
                        value={changes.bonus.amount}
                        onChange={(e) => setChange('bonus', { amount: e.target.value })}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">{currency}</InputAdornment>
                          ),
                        }}
                        sx={{ maxWidth: 160 }}
                      />
                    </Stack>
                  )}
                </Stack>
              </ChangeRow>
            </Section>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box
        sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            {error}
            {flaggedCount > 0 && ' The affected shifts are highlighted — untick them or adjust the changes.'}
          </Alert>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
        >
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={700}>
              {!anyChange
                ? `${targets.length} shift${targets.length === 1 ? '' : 's'} selected — choose a change`
                : changesError
                  ? changesError
                  : invalidCount
                    ? `${invalidCount} selected shift${invalidCount === 1 ? ' would end' : 's would end'} before ${invalidCount === 1 ? 'it starts' : 'they start'}`
                    : `${willChange.length} of ${targets.length} selected shift${
                      targets.length === 1 ? '' : 's'
                    } will change`}
            </Typography>
            {anyChange && !changesError && !invalidCount && assignedTimeChanges > 0 && (
              <Typography variant="caption" color="text.secondary">
                Staff on {assignedTimeChanges} assigned shift
                {assignedTimeChanges === 1 ? '' : 's'} will be told about their new
                times. Pending applications on moved open shifts will lapse.
              </Typography>
            )}
          </Box>
          <Stack direction="row" gap={1} justifyContent="flex-end" flexShrink={0}>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                saving ||
                !anyChange ||
                !!changesError ||
                invalidCount > 0 ||
                !willChange.length ||
                willChange.length > MASS_UPDATE_MAX_SHIFTS
              }
            >
              {saving
                ? 'Updating…'
                : `Update ${willChange.length} shift${willChange.length === 1 ? '' : 's'}`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </BigModal>
  )
}

export default MassUpdateShifts
